/**
 * Single source of truth for the characters that are invalid in a file name
 * and/or break the Content-Disposition header used during upload/download.
 * Uses the Windows invalid set (the superset) so results stay valid on any
 * host OS. Shared by validation and sanitization so the two never drift apart.
 *
 * Declared WITHOUT the global flag so `.test()` is stateless; the sanitizer
 * uses a module-level global copy (below) derived from the same source, so the
 * character set is still defined in exactly one place.
 */
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/;

/**
 * Global-flag copy derived from INVALID_FILENAME_CHARS, built once at module
 * load. Used only with `String.replace` (never `.test()`), so its `lastIndex`
 * state cannot leak between calls.
 */
const INVALID_FILENAME_CHARS_GLOBAL = new RegExp(INVALID_FILENAME_CHARS, 'g');

/**
 * Returns true when the value contains a character that is not allowed in a
 * file name.
 */
export function hasInvalidFileNameChars(value: string): boolean {
  return !!value && INVALID_FILENAME_CHARS.test(value);
}

/**
 * Replaces every invalid file name character with '_'. Mirrors the backend
 * sanitization so values shown in the UI match the actually downloaded file.
 * Names are assembled from user-entered values (project/scope/vessel names,
 * free text), so any of these characters can legitimately appear.
 */
export function sanitizeFileName(value: string): string {
  return value?.replace(INVALID_FILENAME_CHARS_GLOBAL, '_') ?? value;
}
